import { useMemo, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import { useShiftAddEditStore, useShiftsFilter, useShiftsListStore } from '@libs/store/shifts';
import { useRefreshSyncStore } from '@libs/store/refreshSync';
import { useLocationStore } from '@libs/store/locations';
import { ShiftOld } from '@libs/models/shifts/shift';
import { ShiftsIcon } from '@atoms/icons';
import { EmptyListPlaceholder, isDateSeparator, separatorClass } from '@molecules/dataDisplay';
import { DataGrid, DateSeparator } from '@molecules/dataDisplay';
import {
  GridSortModel,
  GridSortItem,
  DataGridProgress,
  PAGE_SIZE_OPTIONS,
  DEFAULT_PAGE_SIZE,
} from '@molecules/dataDisplay/dataGrid';
import { useShiftListColumns } from './columns';
import { outputShiftDataToConsole } from '@pages/schedule/helpers';
import { DateRangeFilter } from '@libs/store/filters';
import { TIMEZONE_FALLBACK } from '@libs/config';

export function ShiftsTable() {
  const { refreshTrigger } = useRefreshSyncStore();
  const {
    data,
    loadShifts,
    loading: shiftsListLoading,
    initiated: shiftsListInitiated,
    total,
    page,
    perPage,
    setPage,
    setPerPage,
  } = useShiftsListStore();
  const openShiftDrawer = useShiftAddEditStore((s) => s.openShiftDrawer);
  const { activeFilters, getFilter, hasFilter } = useShiftsFilter();
  const rows = useRowsGrouped(data);
  const { current: currentLocation } = useLocationStore();
  const { sortModel, setSortModel, sortFilter } = useSortHandler();

  const loading = shiftsListLoading || !shiftsListInitiated;

  useEffect(() => {
    if (currentLocation) {
      const startDate = getFilter('startDate');
      if (!hasFilter('startDate') || !(startDate as DateRangeFilter)?.value?.from) {
        // If startDate filter is not set, we set it to the current date
        activeFilters.startDate = {
          key: 'startDate',
          value: {
            from: dayjs().startOf('day'),
            timezone: currentLocation?.timezone || TIMEZONE_FALLBACK, // Default timezone, can be adjusted
          },
        };
      }
      loadShifts(
        page,
        perPage,
        {
          ...activeFilters,
          locations: { key: 'locations', value: [currentLocation.id] },
        },
        sortFilter,
        currentLocation.timezone,
      );
    }
  }, [loadShifts, activeFilters, refreshTrigger, sortFilter, currentLocation, page, perPage]);

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <DataGridProgress loading={loading} />
      <DataGrid
        rows={rows}
        rowHeight={64}
        columns={useShiftListColumns()}
        sortModel={sortModel}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: DEFAULT_PAGE_SIZE,
            },
          },
        }}
        onSortModelChange={setSortModel}
        paginationModel={{
          pageSize: perPage,
          page: page,
        }}
        rowCount={total}
        onPaginationModelChange={(params) => {
          if (!shiftsListLoading) {
            setPage(params.page);
            setPerPage(params.pageSize);
          }
        }}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disableRowSelectionOnClick
        getRowHeight={({ model }) => {
          if (model.type === 'separator') {
            return 44;
          }

          return null;
        }}
        getRowClassName={(params) => {
          if (isDateSeparator(params.row)) {
            return separatorClass;
          }
          return '';
        }}
        noRowsOverlay={
          <EmptyListPlaceholder
            icon={<ShiftsIcon active />}
            title={<FormattedMessage id="shifts.table.noRows.title" />}
            subtitle={<FormattedMessage id="shifts.table.noRows.subtitle" />}
          />
        }
        onRowClick={(params, evt) => {
          if (evt.ctrlKey) {
            outputShiftDataToConsole(params.row);
          }
          openShiftDrawer(`${params.id}`);
        }}
      />
    </Box>
  );
}

function useRowsGrouped(data: ShiftOld[]): (ShiftOld | DateSeparator)[] {
  return useMemo(() => {
    const groupedData: (ShiftOld | DateSeparator)[] = [];
    data.forEach((row, index) => {
      const lastItem = data[index - 1];
      const isSameDay = dayjs(row.shiftStart).isSame(dayjs(lastItem?.shiftStart), 'day');
      if (lastItem && isSameDay) {
        groupedData.push(row);
      } else {
        const idValue = dayjs(row.shiftStart).valueOf().toString();
        if (!groupedData.find((item) => item.id === idValue)) {
          groupedData.push({
            id: idValue,
            type: 'separator',
            date: dayjs(row.shiftStart).toDate(),
          });
        }
        groupedData.push(row);
      }
    });

    return groupedData;
  }, [data]);
}

/**
 * @TODO: Split out series and rotations list and shifts list stores.
 * And move this section into ShiftsFilterStore
 */
function useSortHandler() {
  const [sortModel, setSortModel] = useState<GridSortModel>([defaultDateSortModel]);
  const sortFilter = useMemo(
    () => ({
      key: 'orderBy',
      value: sortModel.map((p) => `${propToFilterMap[p.field] ?? p.field}_${p.sort}`),
    }),
    [sortModel],
  );

  return { sortModel, sortFilter, setSortModel };
}

const defaultDateSortModel: GridSortItem = {
  field: 'shiftStart',
  sort: 'asc',
};

const propToFilterMap: Record<string, string> = {
  shiftStart: 'startDate',
};
